import { isUndefined } from "lodash-es";
import { get } from "lodash-es";
import objectHash from "object-hash";
import React, { useEffect, useState } from "react";

import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";

import { CirclePlus } from "../../../components/icons";
import TextField from "../../../components/styled/TextField";
import useListAvailableLogFields from "../../../hooks/useListAvailableLogFields";
import { setSettingsFormProperty } from "../../../slices/settings";
import { useDispatch, useSelector } from "../../../store";
import { useGetSettingsQuery } from "../../../store/enhancedApi";
import { chunkArr } from "../../../utils/chunk";

type LogFieldsProps = {
  field: "accessLogFields" | "authorizeLogFields";
  disabled: boolean;
};
const LogFields = ({ field, disabled }: LogFieldsProps) => {
  const dispatch = useDispatch();
  const { organizationId, clusterId } = useSelector((state) => state.current);
  const [selected, setSelected] = useState<string[] | undefined>(undefined);
  const [addInput, setAddInput] = useState("");
  const [customOptions, setCustomOptions] = useState<string[]>([]);
  const { data: settings } = useGetSettingsQuery(
    {
      organizationId,
      clusterId,
    },
    {
      skip: !organizationId || !clusterId,
    },
  );
  const { data: listAvailableLogFieldsResponse } = useListAvailableLogFields();
  const options =
    field === "accessLogFields"
      ? listAvailableLogFieldsResponse?.availableAccessLogFields || []
      : listAvailableLogFieldsResponse?.availableAuthorizeLogFields || [];
  const defaultOptions =
    field === "accessLogFields"
      ? listAvailableLogFieldsResponse?.defaultAccessLogFields || []
      : listAvailableLogFieldsResponse?.defaultAuthorizeLogFields || [];

  useEffect(() => {
    const setOptions = get(settings, field, undefined);
    setSelected(setOptions);
    setCustomOptions(
      (setOptions || []).filter((s) => options.indexOf(s) === -1),
    );
  }, [objectHash(settings || {})]);

  const setFieldsFromSwitch = () => {
    setSelected((lv) => {
      const ret = isUndefined(lv) ? defaultOptions : undefined;
      dispatch(setSettingsFormProperty({ path: field, value: ret }));
      return ret;
    });
  };

  const handleToggle = (value: string) => {
    const index = selected?.indexOf(value);
    let newChecked = [...(selected || [])];

    if (index === -1 || typeof index === "undefined") {
      newChecked.push(value);
    } else {
      newChecked.splice(index, 1);
    }

    if (value === "headers") {
      newChecked = newChecked.filter((s) => !s.startsWith("headers."));
    }

    if (value.startsWith("headers.")) {
      const allHeadersIndex = newChecked.indexOf("headers");
      if (allHeadersIndex > -1) {
        newChecked.splice(allHeadersIndex, 1);
      }
    }

    setSelected(newChecked);
    dispatch(setSettingsFormProperty({ path: field, value: newChecked }));
  };

  const addCustomOption = () => {
    if (!isError(addInput) && addInput) {
      const newCustomOptions = [...customOptions];
      newCustomOptions.push(addInput);
      setCustomOptions(newCustomOptions);
      const newChecked = [...(selected || [])];
      newChecked.push(addInput);
      const allHeadersIndex = newChecked.indexOf("headers");
      if (allHeadersIndex > -1) {
        newChecked.splice(allHeadersIndex, 1);
      }
      setSelected(newChecked);
      setAddInput("");
      dispatch(setSettingsFormProperty({ path: field, value: newChecked }));
    }
  };

  const keyPress = (e: { keyCode: number }) => {
    if (e.keyCode == 13) {
      addCustomOption();
    }
  };

  const isError = (val: string): boolean => {
    return (
      customOptions.indexOf(val) > -1 || (!!val && !val.startsWith("headers."))
    );
  };

  const errorText = (val: string) => {
    if (!val) {
      return " ";
    }
    if (customOptions.indexOf(val) > -1) {
      return "Field already exists!";
    }
    if (!val.startsWith("headers.")) {
      return 'Must start with "headers."';
    }
    return " ";
  };

  const threeInRow = chunkArr(options.concat(customOptions), 3);

  return (
    <Box sx={{ width: "100%" }}>
      <Stack direction="row" justifyContent="space-between">
        <RadioGroup
          aria-label="core-or-custom"
          name="core-or-custom"
          row
          value={isUndefined(selected)}
          onChange={setFieldsFromSwitch}
        >
          <Tooltip
            title={`Uses the following Defaults from Core Pomerium: ${defaultOptions.join(", ")}`}
            placement="top-start"
          >
            <FormControlLabel
              value="true"
              control={<Radio disabled={disabled} />}
              label="Core Defaults"
            />
          </Tooltip>
          <Tooltip
            title={`Whitelist only the fields you want to log. Add custom headers with dot notation!`}
            placement="top-start"
          >
            <FormControlLabel
              value="false"
              control={<Radio disabled={disabled} />}
              label="Custom Logged Fields"
            />
          </Tooltip>
        </RadioGroup>
        {!isUndefined(selected) && (
          <Stack direction="row" justifyContent="right" alignItems="center">
            <TextField
              disabled={disabled}
              fullWidth
              size="small"
              error={isError(addInput)}
              value={addInput}
              onChange={(e) => setAddInput(e.target.value)}
              helperText={errorText(addInput)}
              placeholder="headers.content-type"
              onKeyDown={keyPress}
            />
            <Tooltip
              title="Add individual custom headers by using dot notation. Ex: headers.content-type"
              placement="top-start"
            >
              <span>
                <IconButton
                  size="small"
                  disabled={disabled}
                  onMouseUp={addCustomOption}
                  sx={{ pb: 3 }}
                >
                  <CirclePlus />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        )}
      </Stack>
      {!isUndefined(selected) &&
        threeInRow.map((row, i) => {
          return (
            <Stack direction="row" key={field + "_row_" + i}>
              {[0, 1, 2].map((j) => {
                const value = get(row, j, "");
                const labelId = `checkbox-list-label-${value}`;
                return (
                  <List
                    key={field + "_row_" + i + "_checkbox_" + j}
                    sx={{
                      width: "100%",
                      maxWidth: 360,
                    }}
                  >
                    {!!value && (
                      <ListItem disablePadding>
                        <ListItemButton
                          disabled={disabled}
                          role={undefined}
                          onClick={() => handleToggle(value)}
                          dense
                        >
                          <ListItemIcon>
                            <Checkbox
                              disabled={disabled}
                              edge="start"
                              checked={selected?.indexOf(value) !== -1}
                              tabIndex={-1}
                              disableRipple
                              inputProps={{ "aria-labelledby": labelId }}
                            />
                          </ListItemIcon>
                          <ListItemText id={labelId} primary={value} />
                        </ListItemButton>
                      </ListItem>
                    )}
                  </List>
                );
              })}
            </Stack>
          );
        })}
    </Box>
  );
};
export default LogFields;
